import { Title } from "@components/_Revamp/Typography/Title.stories";
import { css } from "@linaria/core";
import { styled } from "@linaria/react";
import MainContainer from "@pages/EverGreen/Components/Container";
import InnerContainer from "@pages/EverGreen/Components/InnerContainer";
import { SCREEN_SIZE } from "@static/screen-size";
import { EverGreenStyle } from "@utils/Style/everGreen";
import Modal from "@components/_Revamp/Modal";
import LightBulb from "@assets/icon/light-bulb.svg";
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAvivaSelector } from "@redux/hook";
import { debounce, get } from "lodash";
import axios, {
  IEvergreenSQSQuotation,
  getThrencheLimit,
  postEvergreenQuotation,
} from "@api/index";
import { useDispatch } from "react-redux";
import {
  updateInvestAmount,
  updatePlanData,
  updateSqsQuotation,
  updateTransactionId,
} from "@redux/slices/EVER_GREEN/step1Slice";
import { useHistory } from "react-router-dom";
import { ROUTE_PATH } from "@static/route-paths";
import { CancelTokenSource } from "axios";
import Button from "@pages/EverGreen/Components/Button";
import tooltipWhite from "@assets/icon/tooltip-i-white.svg";
import { Tooltip } from "@pages/EverGreen/Components/Tooltip";
import tooltipgreen from "@assets/icon/tooltip-i-grey.svg";
import TrenchesLimit from "@pages/EverGreen/Components/TrenchesLimit";
import { EVER_GREEN_STEP_LIST } from "@static/content";
import { format } from "@pages/EverGreen/Helpers/helpers";
import { generatePlanOver } from "@pages/EverGreen/Helpers/generatePlanOverview";
import { LoadingScreen } from "@utils/helpers";
import {
  trackOnFormError,
  trackOnFormStartOrSuccess,
  trackOnLoad,
} from "@pages/EverGreen/Helpers/analyticsHelper";

function Quotation() {
  const isFocus = useRef(false);
  const amountRange = useAvivaSelector(
    (state) => state.everGreenStep1.amountRangeSlider
  );
  const transactionId = useAvivaSelector(
    (state) => state.everGreenStep1.transactionId
  );
  const minimumAmount = useMemo(() => {
    return amountRange.minimumAmountRange !== -1
      ? amountRange.minimumAmountRange
      : 20000;
  }, [amountRange]);
  const maximumAmount = useMemo(() => {
    return amountRange.maximumAmountRange !== -1
      ? amountRange.maximumAmountRange
      : 200000;
  }, [amountRange]);
  const amountInputref = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const sqsquote = useAvivaSelector(
    (state) => state.everGreenStep1.quotation.sqsquotation
  );
  const initialTermsAcceptance = useAvivaSelector(
    (state) => state.everGreenStep1.AgreedInitialTerms
  );
  const premiumAmount = useAvivaSelector(
    (state) => state.everGreenStep1.quotation.investingAmount
  );
  const quotation = useAvivaSelector((state) => state.everGreenStep1.quotation);
  const [amount, setAmount] = useState(() =>
    premiumAmount > 0 ? premiumAmount : minimumAmount
  );
  const amountRef = useRef({ amount, prevAmount: -1 });
  const history = useHistory();
  const cancelToken = useRef<CancelTokenSource>();
  const [buttonDisable, setDisableState] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const staticData = useAvivaSelector(
    (state) => state.everGreenStep1.staticData
  );

  const closeModal = useCallback(() => {
    window.location.replace(ROUTE_PATH.EVER_GREEN_LANDING_PAGE);
    dispatch(
      updateSqsQuotation({
        deathBenefit: -1,
        maturityPayout: -1,
        surrenderValue: -1,
        surrenderValueList: [],
      })
    );

    setShowModal(false);
  }, []);

  const sqsApiCall = useCallback(
    debounce(async (transactionId: string, value: number) => {
      try {
        let transaction_id;
        cancelToken.current?.cancel("Previous request cancelled");
        cancelToken.current = axios.CancelToken.source();
        if (!transactionId) {
          transaction_id = crypto.randomUUID();
          dispatch(updateTransactionId(transaction_id));
        }
        const payload: IEvergreenSQSQuotation = {
          customerId: 0,
          premiumAmount: value as number,
          transactionId: transactionId || (transaction_id as string),
        };
        const [{ data }] = await Promise.all([
          postEvergreenQuotation(payload, cancelToken.current),
        ]);
        dispatch(updateSqsQuotation(data));
        dispatch(updateInvestAmount(value));
        amountRef.current.prevAmount = amountRef.current.amount;
        dispatch(updatePlanData(generatePlanOver()));
        generatePlanOver();
        // trackOnFormStartOrSuccess("DS_quotation_form")
      } catch (e) {
        if (!axios.isCancel(e)) {
          //If NOT cancelled because of signal don't have to do anything.
          //Route user to error page(contact us) page when error occurs.
          // history.push(ROUTE_PATH.EVER_GREEN_CONTACTUS)
        }
      }
    }, 500),
    []
  );

  const amounHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputType = e.target.type;
    let value: number | string = e.target.value
      .split(".")[0]
      .replaceAll(",", "");
    value = Number(value);
    amountRef.current.amount = value;
    if (value > maximumAmount) {
      value = maximumAmount;
    }

    amountRef.current.prevAmount = value;
    if (amountInputref.current?.type === "text") {
      amountInputref.current.value = formatter(value, isFocus.current);
    }
    setAmount(value);
    if (inputType !== "range") return;
    sqsApiCall(transactionId as string, value as number);
  };
  const onBlur = () => {
    isFocus.current = false;
    let valAsNum = parseFloat(
      amountInputref.current?.value?.replaceAll(",", "") || "0"
    );

    let flag = false;
    if (valAsNum < minimumAmount) {
      setAmount(minimumAmount);
      amountRef.current.amount = minimumAmount;
      flag = true;
    } else if (valAsNum > maximumAmount) {
      amountRef.current.amount = maximumAmount;
      setAmount(maximumAmount);
      flag = true;
    } else if (amountInputref.current?.type === "text") {
      amountRef.current.amount = valAsNum;
      setAmount(valAsNum);
    }
    if (amountInputref.current?.type === "text")
      amountInputref.current.value = formatter(
        amountRef.current.amount,
        isFocus.current
      );

    if (amountInputref.current?.type === "text") {
      let roundOffnumber = Math.ceil(amountRef.current.amount);
      if (roundOffnumber % 1000 !== 0 && !flag) {
        let num = Math.ceil(roundOffnumber / 1000);
        num = parseInt(num.toFixed(0)) * 1000;
        setAmount(num);
        amountRef.current.amount = num;
        amounHandler({
          target: { value: num.toString(), type: "range" },
        } as ChangeEvent<HTMLInputElement>);
        if (amountInputref.current?.type === "text") {
          amountInputref.current.value = formatter(num, isFocus.current);
        }
      }
    }
    if (amountRef.current.prevAmount !== amountRef.current.amount) {
      sqsApiCall(transactionId as string, amountRef.current.amount as number);
    }
  };

  const formatter = useCallback(format, []);
  const minmaxlabel = useRef({
    min: formatter(minimumAmount, isFocus.current)?.split(".")[0],
    max: formatter(maximumAmount, isFocus.current)?.split(".")[0],
  });

  const formatAmountLabel = (amount: number) => {
    //convert to string and split number and keep the decimal place valu separate
    const [wholenum, decimalnum] = amount.toFixed(2).toString().split(".");

    let formatedNum = formatter(parseInt(wholenum), true);

    if (decimalnum !== undefined) {
      return formatedNum.concat(`.${decimalnum}`);
    } else {
      return formatedNum.concat(".00");
    }
  };

  const handleProceed = async () => {
    try {
      LoadingScreen();
      const { data: limit } = await getThrencheLimit(
        amount as number,
        undefined
      );
      if (limit.trancheLimit) {
        setShowModal(true);
      } else {
        setShowModal(false);
        trackOnFormStartOrSuccess("DS_quotation_form", true);
        history.push(ROUTE_PATH.EVER_GREEN_VIEW_QUOTE);
      }
    } catch (e) {
      trackOnLoad({
        errorPage: { errorCode: "500", errorMsg: "Trenches limit api failed" },
      });
      history.push(ROUTE_PATH.EVER_GREEN_CONTACTUS);
    } finally {
      LoadingScreen(false);
    }
  };

  useEffect(() => {
    if (
      sqsquote.deathBenefit >= 0 &&
      sqsquote.maturityPayout >= 0 &&
      sqsquote.surrenderValueList?.length >= 0
    ) {
      setDisableState(false);
    } else {
      setDisableState(true);
    }
  }, [sqsquote]);

  useEffect(() => {
    let num = formatter(amount);
    function KeyPressed(e: KeyboardEvent) {
      console.error(e.key);
      if (e.key === "Enter") onBlur();
    }
    if (amountInputref.current?.type === "text") {
      amountInputref.current.value = num;
      amountInputref.current.addEventListener("keyup", KeyPressed);
    }

    const { deathBenefit, maturityPayout, surrenderValue, surrenderValueList } =
      quotation.sqsquotation;

    if (
      deathBenefit === -1 ||
      maturityPayout === -1 ||
      surrenderValue === -1 ||
      surrenderValueList.length === 0
    ) {
      sqsApiCall(transactionId || "", amount);
    }

    return () => {
      amountInputref?.current?.removeEventListener("keyup", KeyPressed);
      cancelToken?.current?.cancel("Previous request cancelled");
    };
  }, []);

  //AA on load
  useEffect(() => {
    if (initialTermsAcceptance === null || initialTermsAcceptance === false)
      return;
    trackOnLoad();
  }, []);

  if (initialTermsAcceptance === null || initialTermsAcceptance === false) {
    history.replace(ROUTE_PATH.EVER_GREEN_GET_STARTED);
    return null;
  }

  return (
    <>
      <MainContainer
        showFlowerPot={true}
        backButtonPath={ROUTE_PATH.EVER_GREEN_GET_STARTED}
        step={1}
      >
        <TitleContainer>
          <Title variant="title" align="center">
            Customise your quote
          </Title>
          <Title14
            style={{
              textAlign: "center",
              color: EverGreenStyle.colors.grey[1200],
            }}
          >
            Decide how much premium to set aside and see how much guaranteed
            payout you'll receive at the end of {staticData.policyTerm} years.
          </Title14>
        </TitleContainer>
        <Container>
          <InnerContainer className={InnerConainterCss}>
            <Title14>
              <img src={LightBulb} alt="light bulb" />
              <span>What you should know</span>
            </Title14>
            <p>
              Before buying any insurance, please consider if the different
              types of Direct Purchase Insurance (DPI) and life policies are
              suitable for your finances and needs.
            </p>
            <p>
              {`Refer to the `}
              <a
                href="https://www.moneysense.gov.sg/planning-your-finances-well"
                target="_blank"
                rel="noreferrer noopener"
              >
                Basic Financial Planning Guide
              </a>{" "}
              on how you can address your savings, insurance and investment
              needs at different life stages.
            </p>
            <p>
              Take the time to{" "}
              <a
                href="https://www.comparefirst.sg/wap/homeEvent.action"
                target="_blank"
                rel="noreferrer noopener"
              >
                compare
              </a>{" "}
              the benefits and costs of the policy you're buying with other
              types of life policies.
            </p>
          </InnerContainer>
          <InnerContainer className={PriceRangeCss}>
            <div className="top-half">
              <Title14>I would like to set aside</Title14>
              <AmountContianer>
                <span>S$</span>
                <AmountInput
                  inputMode="numeric"
                  onFocus={() => {
                    isFocus.current = true;
                    trackOnFormStartOrSuccess("DS_quotation_form");
                  }}
                  onBlur={onBlur}
                  type="text"
                  size={8}
                  ref={amountInputref}
                />
              </AmountContianer>
              <div className="text-premium">
                This will be your single premium amount.
              </div>
              <SliderContianer>
                <div className="labels">
                  <span>Min. S$ {minmaxlabel.current.min}</span>
                  <span>Max. S$ {minmaxlabel.current.max}</span>
                </div>
                <Slider
                  amountRange={
                    amountRef.current.amount > minimumAmount
                      ? Math.round(
                          (amountRef.current.amount / maximumAmount) * 100
                        )
                      : 0
                  }
                  step={1000}
                  min={minimumAmount}
                  max={maximumAmount}
                  value={amount}
                  onChange={(e) => {
                    trackOnFormStartOrSuccess("DS_quotation_form");
                    amounHandler(e);
                  }}
                  type="range"
                />
              </SliderContianer>
            </div>
            <div className="bottom">
              <div className="quote-value">
                <span>You will receive</span>
                <div className="quote-number">
                  <span className="amount">
                    {`S$ ${formatAmountLabel(sqsquote.maturityPayout)}`}
                  </span>
                  <span className="img-tooltip">
                    <span>at the end of {staticData.policyTerm} years</span>
                  </span>
                </div>
              </div>
            </div>

            <GridSplitup>
              <span className="splitup-label">
                <h3>Policy Value and Benefits Overview</h3>

                <div>
                  <span>Cash Surrender Value</span>
                  <span>
                    <Tooltip
                      title="Cash Surrender Value"
                      inlineComponent={
                        <>
                          <img src={tooltipgreen} alt="tooltip" />
                        </>
                      }
                    >
                      <p>
                        This is the amount that you'll receive if you terminate
                        the policy before its maturity date.
                      </p>
                    </Tooltip>
                  </span>
                </div>
                <div>
                  <ul>
                    {sqsquote.surrenderValueList?.map((cs) => (
                      <li key={`cs-${cs.year}`}>
                        <span>
                          <div>Year {cs.year}</div>{" "}
                          <div> S${formatAmountLabel(cs.amount)}</div>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </span>

              <span className="splitup-label">
                <div>
                  Death Benefit
                  <Tooltip
                    title="Death Benefit"
                    inlineComponent={
                      <>
                        <img src={tooltipgreen} alt="tooltip" />
                      </>
                    }
                  >
                    <p>
                      This is the amount we'll pay out if the life assured dies
                      while the policy is in-force.
                    </p>
                  </Tooltip>
                </div>
                <div>S${formatAmountLabel(sqsquote.deathBenefit)}</div>
              </span>
              <span className="splitup-label">
                <div>
                  Maturity Benefit
                  <Tooltip
                    title="Maturity Benefit"
                    inlineComponent={
                      <>
                        <img src={tooltipgreen} alt="tooltip" />
                      </>
                    }
                  >
                    <p>
                      This is the amount you'll receive if you hold the policy
                      till the policy maturity date.
                    </p>
                  </Tooltip>
                </div>
                <div>S${formatAmountLabel(sqsquote.maturityPayout)}</div>
              </span>
            </GridSplitup>
          </InnerContainer>
        </Container>
        <ButtonContianer>
          <Button
            variant="default"
            label="Get my quote"
            disabled={buttonDisable}
            onClick={handleProceed}
          />
        </ButtonContianer>
      </MainContainer>
      {showModal && (
        <Modal className={modalPadding} show={showModal}>
          <TrenchesLimit closeModal={closeModal} />
        </Modal>
      )}
    </>
  );
}

export default Quotation;
const ButtonContianer = styled.div`
  margin: auto;
  margin-top: 230px;
  width: 100%;
  max-width: 182px;
`;
const GridSplitup = styled.div`
  display: grid;
  position: absolute;
  bottom: -64px;
  height: 42px;
  box-sizing: border-box;
  font-size: ${EverGreenStyle.title12};
  color: ${EverGreenStyle.colors.grey[1400]};
  font-weight: 600;
  max-width: 486px;
  row-gap: 8px;
  width: 100%;
  padding: 0px 24px;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr 1fr;

  .splitup-label {
    display: flex;
    justify-content: space-between;
    h3 {
      font-size: 16px;
      font-weight: 600;
      color: ${EverGreenStyle.colors.primary};
      line-height: 22.4px;
      letter-spacing: 0.24px;
    }
    div {
      display: flex;
      gap: 6px;
    }
  }
  .splitup-label:nth-child(1) {
    flex-direction: column;
    gap: 6px;

    div {
      display: flex;
      gap: 6px;
      ul {
        padding-left: 20px;
        width: 100%;
        li {
          span {
            width: 100%;

            display: flex;
            justify-content: space-between;
          }
        }
      }
    }
  }

  .splitup-label:nth-child(2) {
    div {
      display: flex;
    }
  }
`;

const PriceRangeCss = css`
  width: 100%;
  position: relative;
  max-width: 486px;
  padding: 0px;
  display: flex;
  flex-direction: column;
  .top-half {
    display: flex;
    box-sizing: border-box;
    width: 100%;
    max-width: 486px;
    flex-direction: column;
    align-items: center;
    margin: auto;
    padding: 24px;
    p:first-child {
      margin-bottom: 10px;
    }
    .text-premium {
      font-weight: 600;
      font-size: ${EverGreenStyle.title12};
      margin-top: 12px;
    }
  }

  .bottom {
    height: 100%;
    background-color: ${EverGreenStyle.colors.primary};
    box-sizing: border-box;
    max-height: 99px;
    border-bottom-left-radius: 24px;
    border-bottom-right-radius: 24px;
    width: 100%;
    padding: 24px;

    width: 100%;
    color: #fcfcfc;
    .quote-value {
      display: flex;
      justify-content: space-between;
      line-height: 22.4px;
      font-weight: 600;
      span {
        font-size: ${EverGreenStyle.title14};
      }
      .quote-number {
        display: flex;
        flex-direction: column;
        position: re;
        gap: 4px;
        align-items: flex-end;
        .amount {
          font-size: ${EverGreenStyle.title18};
          letter-spacing: 1.5px;
          line-height: 25.2px;
        }
        .img-tooltip {
          display: flex;
          align-items: center;
          gap: 6px;

          span {
            font-size: 12px;
            font-weight: 600;
            line-height: 16.8px;
            display: flex;
          }
        }
      }
    }
  }

  @media screen {
    max-width: 486px;
  }
`;

const SliderContianer = styled.div`
  width: 100%;
  margin-top: 20px;
  .labels {
    display: flex;
    justify-content: space-between;
    font-weight: 600;
    font-size: ${EverGreenStyle.title14};
  }
`;

const Slider = styled.input<{ amountRange: number }>`
  outline: none;
  width: 100%;
  appearance: none;
  transition: 2ms;
  height: 6px;
  background: #dff7f6;
  background-image: linear-gradient(#19d3c5, #19d3c5);
  background-size: ${(prop) =>
    prop.amountRange > 0 ? `${prop.amountRange - 5}% 100%` : `0% 0%`};
  background-repeat: no-repeat;
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;
  border-bottom-left-radius: 3px;
  border-bottom-right-radius: 3px;
  max-width: 438px;
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    height: 24px;
    width: 23px;
    border-radius: 50%;
    background: ${EverGreenStyle.colors.primary};
    cursor: ew-resize;
    box-shadow: 0 0 2px 0 #555;
  }
  &::-moz-range-thumb {
    height: 24px;
    width: 24px;
    border-radius: 50%;
    background: ${EverGreenStyle.colors.primary};
    cursor: ew-resize;
    box-shadow: 0 0 2px 0 #555;
  }
  &::-ms-thumb {
    height: 24px;
    width: 24px;
    border-radius: 50%;
    background: ${EverGreenStyle.colors.primary};
    cursor: ew-resize;
    box-shadow: 0 0 2px 0 #555;
  }
  &::-webkit-slider-thumb:hover {
    background: ${EverGreenStyle.colors.primary};
  }
  &::-moz-range-thumb:hover {
    background: ${EverGreenStyle.colors.primary};
  }
  &::-ms-thumb:hover {
    background: ${EverGreenStyle.colors.primary};
  }
  &::-webkit-slider-runnable-track {
    -webkit-appearance: none;
    box-shadow: none;
    border: none;
    background: transparent;
  }
  &::-moz-range-track {
    box-shadow: none;
    border: none;
    background: transparent;
  }
  &::-ms-track {
    box-shadow: none;
    border: none;
    background: transparent;
  }
`;

const Title14 = styled.p`
  font-size: 14px;
  font-weight: 600;
  display: flex;
  justify-items: center;
  align-items: center;
  gap: 8px;
  img {
    width: 24px;
    height: 24px;
    margin-top: -10px;
    display: inline-block;
  }
`;

const AmountContianer = styled.div`
  color: ${EverGreenStyle.colors.primary};
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  /* width: max-content; */
  min-width: 170px;
  justify-content: center;
  font-size: 24px;
  font-weight: 600;
  border-bottom: 1px solid ${EverGreenStyle.colors.grey[1000]};
`;

const AmountInput = styled.input`
  border: none;
  color: ${EverGreenStyle.colors.primary};
  text-align: center;
  font-size: 24px;
  max-width: 160px;
  width: 100%;
  font-weight: 600;
  &:focus {
    outline: none;
  }
`;

const TitleContainer = styled.div`
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const InnerConainterCss = css`
  color: ${EverGreenStyle.colors.grey[500]};
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 486px;
  box-sizing: border-box;
  p:not(:first-child) {
    font-size: ${EverGreenStyle.fontSize.xs}px;
    a {
      color: ${EverGreenStyle.colors.primary};
      font-weight: 600;
    }
  }
  @media screen and (min-width: ${`${SCREEN_SIZE.mobilesUp}px`}) {
    width: 100%;
    box-sizing: unset;
    max-width: 235px;
    margin-right: 20px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    flex-direction: row;
    align-items: unset;
  }
`;
const modalPadding = css`
  .modal-body {
    padding: 24px 16px;
  }
  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    .modal-body {
      padding: 36px;
    }
  }
`;
